export const MAX_GROSS_SEND_USD = 3000;

export type BoundFigures = {
  typedCurrency: string | null;
  minTypedAmount: number | null;
  maxTypedAmount: number | null;
  minReceiveUsd: number | null;
  maxReceiveUsd: number | null;
};

export function boundsPairReady(
  direction: 'recipient' | 'sender',
  bounds: BoundFigures | null,
): boolean {
  if (!bounds) return false;
  if (direction === 'sender') {
    return bounds.minTypedAmount != null && bounds.maxTypedAmount != null;
  }
  return bounds.minReceiveUsd != null && bounds.maxReceiveUsd != null;
}

/**
 * Sender is outside the loaded typed pair. Recipient is outside the loaded receive pair.
 * A missing receive pair still treats an amount above 3,000 USD as out of range.
 * A missing pair does not, by itself, mark a smaller amount out of range.
 */
export function typedAmountBlocked(
  direction: 'recipient' | 'sender',
  amount: number,
  bounds: BoundFigures | null,
): boolean {
  if (!Number.isFinite(amount)) return true;
  if (direction === 'sender') {
    if (bounds?.minTypedAmount == null || bounds.maxTypedAmount == null) return false;
    return amount < bounds.minTypedAmount || amount > bounds.maxTypedAmount;
  }
  if (bounds?.minReceiveUsd == null || bounds.maxReceiveUsd == null) {
    return amount > MAX_GROSS_SEND_USD;
  }
  return amount < bounds.minReceiveUsd || amount > bounds.maxReceiveUsd;
}

export function formatBoundAmount(amount: number): string {
  const negative = amount < 0;
  const [whole, frac] = Math.abs(amount).toFixed(2).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${grouped}.${frac}`;
}

export function fillBoundTemplate(
  template: string,
  values: { min?: string; max?: string; currency?: string },
): string {
  return replaceToken(
    replaceToken(replaceToken(template, '{min}', values.min ?? ''), '{max}', values.max ?? ''),
    '{currency}',
    values.currency ?? '',
  );
}

/** Placeholder is the loaded minimum for that direction. Never a hardcoded 100. */
export function amountPlaceholder(
  direction: 'recipient' | 'sender',
  bounds: BoundFigures | null,
): string {
  if (!bounds) return '';
  const value = direction === 'sender' ? bounds.minTypedAmount : bounds.minReceiveUsd;
  if (value == null) return '';
  return formatBoundAmount(value);
}

/** USD You-send copy omits "at the current rate". */
export function youSendHelper(
  plainTemplate: string,
  rateTemplate: string,
  min: number,
  max: number,
  currency: string,
): string {
  const template = currency.toUpperCase() === 'USD' ? plainTemplate : rateTemplate;
  return fillBoundTemplate(template, {
    min: formatBoundAmount(min),
    max: formatBoundAmount(max),
    currency,
  });
}

export function youSendMaximumSentence(
  plainTemplate: string,
  rateTemplate: string,
  max: number,
  currency: string,
): string {
  const template = currency.toUpperCase() === 'USD' ? plainTemplate : rateTemplate;
  return fillBoundTemplate(template, { max: formatBoundAmount(max), currency });
}

function readBoundAmount(value: unknown): number | null {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * A present null clears that figure. A body with none of the four keys keeps the last pair.
 */
export function mergeEstimateBounds(
  previous: BoundFigures | null,
  incoming: object | null | undefined,
): BoundFigures | null {
  if (!incoming) return previous;
  const record = incoming as Record<string, unknown>;
  const keys = ['minTypedAmount', 'maxTypedAmount', 'minReceiveUsd', 'maxReceiveUsd'] as const;
  if (!keys.some((key) => Object.prototype.hasOwnProperty.call(record, key))) {
    return previous;
  }
  const next: BoundFigures = {
    typedCurrency: previous?.typedCurrency ?? null,
    minTypedAmount: previous?.minTypedAmount ?? null,
    maxTypedAmount: previous?.maxTypedAmount ?? null,
    minReceiveUsd: previous?.minReceiveUsd ?? null,
    maxReceiveUsd: previous?.maxReceiveUsd ?? null,
  };
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(record, key)) {
      next[key] = readBoundAmount(record[key]);
    }
  }
  if (typeof record.typedCurrency === 'string' && record.typedCurrency.trim()) {
    next.typedCurrency = displayCurrency(record.typedCurrency);
  } else if (typeof record.sourceCurrencyCode === 'string') {
    next.typedCurrency = displayCurrency(record.sourceCurrencyCode);
  }
  return next;
}

/** USD value of the amount the sender pays. That amount already includes the developer fee. */
export function grossSendUsd(requiredSendAmount: number, rateToUsd: number): number {
  return Math.round(requiredSendAmount * rateToUsd * 100) / 100;
}

export function exceedsGrossSendCap(
  requiredSendAmount: number,
  rateToUsd: number,
  cap = MAX_GROSS_SEND_USD,
): boolean {
  if (!(rateToUsd > 0) || !Number.isFinite(requiredSendAmount)) return true;
  return grossSendUsd(requiredSendAmount, rateToUsd) > cap;
}

export function quoteExceedsGrossSendCap(estimate: {
  requiredSendAmount?: number | null;
  bridgeBuyRateToUsd?: number | null;
  sourceSellRateToUsd?: number | null;
  sourceCurrencyCode?: string | null;
}): boolean {
  const amount = estimate.requiredSendAmount;
  if (amount == null || Number.isNaN(Number(amount))) return false;
  const explicit = estimate.sourceSellRateToUsd ?? estimate.bridgeBuyRateToUsd;
  const rate = explicit ?? (displayCurrency(estimate.sourceCurrencyCode) === 'USD' ? 1 : null);
  if (rate == null || !(Number(rate) > 0)) return true;
  return exceedsGrossSendCap(Number(amount), Number(rate));
}

export function displayCurrency(code: string | null | undefined): string {
  const trimmed = (code ?? '').trim();
  if (trimmed.toUpperCase() === 'USDC') return 'USD';
  return trimmed;
}

export function formatMoney(amount: number | null | undefined, currency: string | null | undefined): string {
  if (amount == null || Number.isNaN(Number(amount))) return '';
  const code = displayCurrency(currency);
  const figure = Number(amount).toFixed(2);
  return code ? `${figure} ${code}` : figure;
}

export function formatPercent(percent: number | null | undefined): string {
  if (percent == null || Number.isNaN(Number(percent))) return '';
  return Number(percent).toFixed(2);
}

export function showsSourceFxLine(sourceCurrencyCode: string | null | undefined): boolean {
  return displayCurrency(sourceCurrencyCode) !== 'USD';
}

export function formatBridgeRate(
  sourceCurrencyCode: string | null | undefined,
  rate: number | null | undefined,
): string {
  if (!showsSourceFxLine(sourceCurrencyCode) || rate == null || Number.isNaN(Number(rate)) || Number(rate) <= 0) {
    return '';
  }
  return String(rate);
}

function replaceToken(template: string, token: string, value: string): string {
  return template.split(token).join(value);
}

export function feeLine(
  template: string,
  feeFormatted: string,
  percent: number | null | undefined,
): string {
  return replaceToken(replaceToken(template, '{fee}', feeFormatted), '{percent}', formatPercent(percent));
}

export function bridgeRateLine(template: string, source: string, rate: string): string {
  return replaceToken(replaceToken(template, '{source}', source), '{rate}', rate);
}

export function modeLabel(mode: string, localCode: string | null | undefined, localFallback: string): string {
  if (mode.toUpperCase() === 'USD') return 'USD';
  if (mode.toUpperCase() === 'LOCAL') {
    const code = displayCurrency(localCode);
    return code || localFallback;
  }
  return displayCurrency(mode) || mode;
}
